import { useEffect, useMemo, useState } from 'react';
import { formatOperationTypeLabel } from '../utils/photorealistic/historyFormat';

function getHistoryImageCandidates(item) {
  return [item?.url, item?.imageKey, item?.s3Key].filter(
    (v, i, arr) => typeof v === 'string' && v.trim() !== '' && arr.indexOf(v) === i
  );
}

/** Relative label for recent history; after ~1 week show a short date only. */
function formatHistoryRelativeTime(createdAt) {
  if (!createdAt) return '';
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return '';
  const now = Date.now();
  const diffMs = now - t;
  const absDay = 24 * 60 * 60 * 1000;
  const dateOnly = () =>
    new Date(createdAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  if (diffMs < 0) return dateOnly();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return sec < 15 ? 'Just now' : `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return min === 1 ? '1 min ago' : `${min} mins ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return day === 1 ? '1 day ago' : `${day} days ago`;
  return dateOnly();
}

const RoomHistoryPanel = ({
  historyLoading,
  historyError,
  historyImages,
  activeImageId,
  previewImageId,
  onPreviewHistoryItem,
  onRestoreHistoryItem,
}) => {
  const [failedUrlIndexByImageId, setFailedUrlIndexByImageId] = useState({});

  useEffect(() => {
    const validIds = new Set((historyImages ?? []).map((x) => String(x?.id)));
    setFailedUrlIndexByImageId((prev) => {
      const next = {};
      Object.entries(prev).forEach(([id, idx]) => {
        if (validIds.has(id)) next[id] = idx;
      });
      return next;
    });
  }, [historyImages]);

  const sortedHistoryImages = useMemo(() => {
    const list = Array.isArray(historyImages) ? [...historyImages] : [];
    return list.sort((a, b) => {
      const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
  }, [historyImages]);

  return (
    <>
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 shrink-0">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
      History
    </h3>
    <div className="flex-1 min-h-0 overflow-y-auto">
      {historyLoading ? (
        <p className="text-gray-500 text-sm py-2">Loading...</p>
      ) : historyError ? (
        <p className="text-red-600 text-sm py-2">{historyError}</p>
      ) : historyImages.length === 0 ? (
        <p className="text-gray-500 text-sm py-2">No History Found</p>
      ) : (
        <ul className="space-y-2">
          {sortedHistoryImages.map((item) => {
            const opType = item.operationType;
            const createdAt = item.createdAt;
            const prompt = item.prompt;
            const imageIdKey = String(item?.id);
            const candidates = getHistoryImageCandidates(item);
            const failedIndex = Number(failedUrlIndexByImageId[imageIdKey] ?? 0);
            const imageUrl = candidates[failedIndex] ?? null;
            const isActive = Number(item?.id) === Number(activeImageId);
            const isPreview = Number(item?.id) === Number(previewImageId);
            return (
              <li key={item.id}>
                <div
                  className={`w-full text-left py-2.5 px-2.5 rounded-lg border transition-colors ${
                    isActive
                      ? 'border-primary-300 bg-primary-50/60'
                      : isPreview
                        ? 'border-gray-400 bg-gray-100/70'
                      : 'border-gray-200 bg-gray-50/50 hover:bg-gray-100'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onPreviewHistoryItem?.(item)}
                    className="w-full text-left"
                    title={isActive ? 'Current base image' : 'Preview image on canvas'}
                  >
                    <div className="flex items-start gap-2">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt="History item"
                          className="h-full w-full object-cover"
                          loading="lazy"
                          draggable={false}
                          crossOrigin="anonymous"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            setFailedUrlIndexByImageId((prev) => ({
                              ...prev,
                              [imageIdKey]: failedIndex + 1,
                            }));
                          }}
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-[10px] text-gray-400 px-1 text-center">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-800 truncate">
                        {formatOperationTypeLabel(opType)}
                      </div>
                      {createdAt && (
                        <div
                          className="text-xs text-gray-500 mt-0.5"
                          title={new Date(createdAt).toLocaleString()}
                        >
                          {formatHistoryRelativeTime(createdAt)}
                        </div>
                      )}
                      {prompt != null && prompt !== '' && (
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2" title={prompt}>
                          {prompt}
                        </div>
                      )}
                      {isActive && (
                        <div className="text-[10px] text-primary-400 font-medium mt-1">Current</div>
                      )}
                      {!isActive && isPreview && (
                        <div className="text-[10px] text-gray-600 font-medium mt-1">Previewing</div>
                      )}
                    </div>
                    </div>
                  </button>
                  {!isActive && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => onRestoreHistoryItem?.(item)}
                        className="text-[11px] font-medium text-gray-700 transition-colors"
                        title="Make this the current image"
                      >
                        Restore
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  </>
  );
};

export default RoomHistoryPanel;
